// ============================================================================
// Webhook Adapter Registry — provider-specific ingress handlers.
//
// Adapters implement two things:
//   1. `verifyWebhook(...)` — confirm the request came from the provider
//      (HMAC / signature check). Return `false` to cause a 401 response.
//   2. `parseWebhook(...)` — turn the raw provider payload into zero or more
//      typed events (dedupe key + capability name + payload).
//
// Signature verification is done BEFORE dedupe/parse. Dedupe uses the
// `eventId` returned by `parseWebhook` plus the instanceId. Parsed events are
// then emitted on the capability event bus.
//
// Registry storage is an in-isolate Map keyed by connector-type slug (not
// id) — routes receive the slug from the URL path and we don't want to force
// an extra DB hop to look up the type id before we even know if the request
// is authentic. The slug is already unique in `connector_types.slug` (unique
// index) so a single-slot Map is safe.
// ============================================================================

import type { CapabilityName } from '../capabilities'
import type { ConnectorInstance } from '../schemas/connectors-instances'
import type { ConnectorType } from '../schemas/connectors-types'

// ============================================================================
// Adapter interface
// ============================================================================

/** Context passed to every adapter invocation. */
export interface WebhookAdapterContext {
  /** Raw request bytes — NEVER re-serialized. Needed for HMAC verification. */
  rawBody: ArrayBuffer
  /** All request headers (lowercased keys per Fetch spec). */
  headers: Headers
  /** The target instance the route resolved from `:instanceId`. */
  instance: ConnectorInstance
  /** The connector type row (holds `webhook_config` + slug). */
  type: ConnectorType
  /** Worker env — adapters read provider-wide secrets (e.g. `FB_APP_SECRET`). */
  env: Record<string, unknown>
}

/**
 * Parsed event record emitted by `parseWebhook`. One ingress request can
 * produce multiple events (e.g. FB batches several messaging events into
 * one webhook delivery).
 */
export interface ParsedWebhookEvent {
  /**
   * Stable provider-assigned id for deduplication. If a provider retries the
   * webhook, the same event must produce the same id. For FB Messenger this
   * is `messaging[].message.mid`; for Line it's `events[].webhookEventId`.
   */
  eventId: string
  /** Provider event type label (e.g. `message.received`, `order.created`). */
  eventType: string
  /** Capability the event will be emitted on (maps to `CapabilityMap`). */
  capability?: CapabilityName
  /** Canonical payload — must already match the capability's event type. */
  payload?: unknown
}

export interface WebhookAdapter {
  /**
   * Verify the request came from the provider. Return false → 401. Must use
   * constant-time comparison for the HMAC or rotate through `timingSafeEqualBytes`
   * from `webhook-verifier.ts`.
   *
   * Throwing is treated as a verification failure (logged + 401), but prefer
   * returning `false` to keep response timings consistent.
   */
  verifyWebhook(ctx: WebhookAdapterContext): Promise<boolean>

  /**
   * Parse the raw payload into canonical events. MUST be idempotent: the
   * same raw body produces the same events in the same order.
   *
   * Return an empty array if the webhook is a valid control message (e.g. FB
   * "test push" with no actionable content) — the route will still record
   * the ingress in the audit log.
   */
  parseWebhook(ctx: WebhookAdapterContext): Promise<ParsedWebhookEvent[]>
}

// ============================================================================
// Registry storage — slug → adapter
// ============================================================================

const registry = new Map<string, WebhookAdapter>()

/**
 * Register a webhook adapter for a connector type (by slug). Later calls with
 * the same slug overwrite the earlier registration — useful for hot-swap in
 * tests. Throws on an empty slug.
 */
export function registerWebhookAdapter(typeSlug: string, adapter: WebhookAdapter): void {
  if (!typeSlug) {
    throw new WebhookAdapterError('INVALID_TYPE_SLUG', 'typeSlug is required')
  }
  if (!adapter || typeof adapter.verifyWebhook !== 'function' || typeof adapter.parseWebhook !== 'function') {
    throw new WebhookAdapterError('INVALID_ADAPTER', 'adapter must implement verifyWebhook + parseWebhook')
  }
  registry.set(typeSlug, adapter)
}

/** Look up a webhook adapter for a slug. Returns null if none registered. */
export function lookupWebhookAdapter(typeSlug: string): WebhookAdapter | null {
  return registry.get(typeSlug) ?? null
}

/** List all registered type slugs — useful for debug / introspection. */
export function registeredWebhookTypeSlugs(): string[] {
  return Array.from(registry.keys())
}

// ============================================================================
// Test hooks
// ============================================================================

/** @internal */
export function __resetWebhookAdapters(): void {
  registry.clear()
}

/** @internal */
export function __webhookAdapterCount(): number {
  return registry.size
}

// ============================================================================
// Errors
// ============================================================================

export type WebhookAdapterErrorCode = 'INVALID_TYPE_SLUG' | 'INVALID_ADAPTER'

export class WebhookAdapterError extends Error {
  constructor(public readonly code: WebhookAdapterErrorCode, message: string) {
    super(message)
    this.name = 'WebhookAdapterError'
  }
}
